import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Incentive revenue → Excel. Every sheet shows recharge and new connection
// count + value (ex-GST, incl. GST) + incentive side by side.
//
//   Monthly report (one year):  Monthly · By partner · Partner × Month
//   Yearly report (5 years):    Yearly · Month-wise · By partner ·
//                               Partner × Year · Partner × Month

const HEAD_BG = "FF0E7490"; // cyan-700
const SUB_BG = "FFCFFAFE"; // cyan-100
const GROUP_BG = "FFECFEFF"; // cyan-50
const TOTAL_BG = "FFF1F5F9"; // slate-100
const BORDER = "FFD1D5DB";
const INR = '"₹"#,##,##0.00';
const border = {
  top: { style: "thin", color: { argb: BORDER } },
  left: { style: "thin", color: { argb: BORDER } },
  bottom: { style: "thin", color: { argb: BORDER } },
  right: { style: "thin", color: { argb: BORDER } },
};

const GROUPS = [
  ["recharge", "Recharge"],
  ["newConn", "New connection"],
  ["total", "Total"],
];
const SUB = ["Count", "Value (ex-GST)", "Value (incl. GST)", "Incentive"];


const r2 = (n) => Math.round(n * 100) / 100;

const emptyBucket = () => ({
  recharge: { count: 0, base: 0, incentive: 0 },
  newConn: { count: 0, base: 0, incentive: 0 },
  count: 0,
  base: 0,
  incentive: 0,
});

const addInto = (acc, b) => {
  ["recharge", "newConn"].forEach((k) => {
    ["count", "base", "incentive"].forEach((f) => {
      acc[k][f] += b?.[k]?.[f] || 0;
    });
  });
  ["count", "base", "incentive"].forEach((f) => {
    acc[f] += b?.[f] || 0;
  });
  return acc;
};

const part = (b, key) =>
  key === "total"
    ? { count: b.count, base: b.base, incentive: b.incentive }
    : b[key] || { count: 0, base: 0, incentive: 0 };

const numbers = (b, gstPct) =>
  GROUPS.flatMap(([key]) => {
    const p = part(b, key);
    const base = Number(p.base || 0);
    return [Number(p.count || 0), r2(base), r2(base * (1 + gstPct / 100)), r2(Number(p.incentive || 0))];
  });

// Months to show for a year: all 12, or up to the current month this year.
const monthsShown = (year) => {
  const now = new Date();
  if (Number(year) > now.getFullYear()) return 0;
  return Number(year) === now.getFullYear() ? now.getMonth() + 1 : 12;
};

/**
 * One formatted table. `labelHeads` are the leading label columns (e.g.
 * ["Partner", "Month"]); each row is { labels: [...], b: bucket, kind? } where
 * kind "subtotal" rows are highlighted and left out of the grand total.
 */
function addTableSheet(wb, name, title, subtitle, labelHeads, rows, gstPct) {
  const L = labelHeads.length;
  const ws = wb.addWorksheet(name, { views: [{ state: "frozen", xSplit: L, ySplit: 5 }] });
  const lastCol = L + GROUPS.length * SUB.length;

  ws.mergeCells(1, 1, 1, lastCol);
  ws.getCell(1, 1).value = title;
  ws.getCell(1, 1).font = { bold: true, size: 14 };
  ws.mergeCells(2, 1, 2, lastCol);
  ws.getCell(2, 1).value = subtitle;
  ws.getCell(2, 1).font = { size: 10, color: { argb: "FF6B7280" } };

  labelHeads.forEach((h, i) => {
    ws.mergeCells(4, i + 1, 5, i + 1);
    ws.getCell(4, i + 1).value = h;
  });
  GROUPS.forEach(([, label], gi) => {
    const c0 = L + 1 + gi * SUB.length;
    ws.mergeCells(4, c0, 4, c0 + SUB.length - 1);
    ws.getCell(4, c0).value = label;
    SUB.forEach((s, si) => {
      ws.getCell(5, c0 + si).value = s;
    });
  });
  [4, 5].forEach((r) => {
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getCell(r, c);
      cell.font = { bold: true, color: { argb: r === 4 ? "FFFFFFFF" : "FF164E63" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: r === 4 ? HEAD_BG : SUB_BG } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = border;
    }
  });

  const total = emptyBucket();
  const kinds = new Map();
  rows.forEach(({ labels, b, kind }) => {
    const row = ws.addRow([...labels, ...numbers(b, gstPct)]);
    if (kind) kinds.set(row.number, kind);
    else addInto(total, b);
  });
  const totalRow = ws.addRow([
    "Total",
    ...Array(L - 1).fill(""),
    ...numbers(total, gstPct),
  ]);
  kinds.set(totalRow.number, "total");

  for (let r = 6; r <= totalRow.number; r += 1) {
    const kind = kinds.get(r);
    for (let c = 1; c <= lastCol; c += 1) {
      const cell = ws.getCell(r, c);
      cell.border = border;
      if (c > L) cell.numFmt = (c - L - 1) % SUB.length === 0 ? "#,##0" : INR;
      if (kind === "total") {
        cell.font = { bold: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_BG } };
      } else if (kind === "subtotal") {
        cell.font = { bold: true, color: { argb: "FF164E63" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: GROUP_BG } };
      }
    }
  }

  labelHeads.forEach((_, i) => {
    ws.getColumn(i + 1).width = i === 0 ? 20 : 12;
  });
  for (let c = L + 1; c <= lastCol; c += 1) {
    ws.getColumn(c).width = (c - L - 1) % SUB.length === 0 ? 10 : 16;
  }
  ws.getRow(5).height = 30;
  return ws;
}

// Rows for "Partner × period": each partner's periods, then a partner subtotal.
function partnerPeriodRows(partnerSeries, periodsOf) {
  const rows = [];
  (partnerSeries || []).forEach(({ partnerCode, series }) => {
    const sub = emptyBucket();
    periodsOf(series).forEach(([label, b]) => {
      rows.push({ labels: [partnerCode, label], b });
      addInto(sub, b);
    });
    rows.push({ labels: [partnerCode, "Total"], b: sub, kind: "subtotal" });
  });
  return rows;
}

/**
 * @param {"month"|"year"} kind
 * @param {object} data  incentive API response for that view
 * @param {string} partnerLabel  "All partners" or the partner code
 * @param {object[]} [monthly]  yearly report only: the view=month response
 *   for each of those years (month-wise sheets)
 */
export async function exportIncentiveExcel(kind, data, partnerLabel, monthly = []) {
  const gstPct = Number(data?.gstPct ?? 18);
  const isMonthly = kind === "month";
  const series = data?.series || [];
  const period = isMonthly
    ? data?.currentLabel
    : `${series[0]?.label || ""}–${series[series.length - 1]?.label || ""}`;
  const reportName = isMonthly ? `Monthly incentive report ${period}` : `Yearly incentive report ${period}`;
  const subtitle = `Partner: ${partnerLabel} · ${data?.from} to ${data?.to} · GST ${gstPct}% · Incentive @ current ${data?.incentivePct ?? 14}% (each event uses the rate in force on its date)`;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Sattis";
  wb.created = new Date();

  if (isMonthly) {
    const shown = monthsShown(data?.currentLabel);
    const months = (s) => s.slice(0, shown).map((m) => [`${m.label} ${period}`, m]);

    addTableSheet(wb, "Monthly", reportName, subtitle, ["Month"],
      months(series).map(([l, b]) => ({ labels: [l], b })), gstPct);
    addTableSheet(wb, "By partner", `${reportName} — by partner`, subtitle, ["Partner"],
      (data?.partners || []).map((p) => ({ labels: [p.partnerCode], b: p })), gstPct);
    addTableSheet(wb, "Partner × Month", `${reportName} — partner-wise by month`, subtitle,
      ["Partner", "Month"], partnerPeriodRows(data?.partnerSeries, (s) => months(s).filter(([, b]) => b.count > 0)), gstPct);
  } else {
    addTableSheet(wb, "Yearly", reportName, subtitle, ["Year"],
      series.map((s) => ({ labels: [s.label], b: s })), gstPct);

    if (monthly.length) {
      // All years, month by month, with a subtotal after each year.
      const rows = [];
      monthly.forEach((yr) => {
        const y = yr.currentLabel;
        const sub = emptyBucket();
        (yr.series || []).slice(0, monthsShown(y)).forEach((m) => {
          rows.push({ labels: [`${m.label} ${y}`], b: m });
          addInto(sub, m);
        });
        rows.push({ labels: [`${y} total`], b: sub, kind: "subtotal" });
      });
      addTableSheet(wb, "Month-wise", `${reportName} — month-wise`, subtitle, ["Month"], rows, gstPct);
    }

    addTableSheet(wb, "By partner", `${reportName} — by partner`, subtitle, ["Partner"],
      (data?.partners || []).map((p) => ({ labels: [p.partnerCode], b: p })), gstPct);
    addTableSheet(wb, "Partner × Year", `${reportName} — partner-wise by year`, subtitle,
      ["Partner", "Year"], partnerPeriodRows(data?.partnerSeries, (s) => s.map((y) => [y.label, y])), gstPct);

    if (monthly.length) {
      // Partner → every month of every year. Merge each year's partnerSeries.
      const byPartner = new Map();
      monthly.forEach((yr) => {
        const y = yr.currentLabel;
        const n = monthsShown(y);
        (yr.partnerSeries || []).forEach(({ partnerCode, series: ps }) => {
          if (!byPartner.has(partnerCode)) byPartner.set(partnerCode, []);
          ps.slice(0, n).forEach((m) => byPartner.get(partnerCode).push({ ...m, label: `${m.label} ${y}` }));
        });
      });
      // Same partner order as the "By partner" sheet (highest incentive first).
      const order = (data?.partners || []).map((p) => p.partnerCode);
      const ps = [...byPartner.entries()]
        .sort(([a], [b]) => (order.indexOf(a) + 1 || 1e9) - (order.indexOf(b) + 1 || 1e9))
        .map(([partnerCode, s]) => ({
          partnerCode,
          // Keep only months with activity, so the sheet stays readable.
          series: s.filter((m) => m.count > 0),
        }));
      addTableSheet(wb, "Partner × Month", `${reportName} — partner-wise by month`, subtitle,
        ["Partner", "Month"], partnerPeriodRows(ps, (s) => s.map((m) => [m.label, m])), gstPct);
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  const slug = `${isMonthly ? "monthly" : "yearly"}-incentive-${period}-${partnerLabel}`
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  saveAs(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `${slug}.xlsx`,
  );
}


